/**
 * ooxml.js — the one owner of "swap the words inside an Office file".
 *
 * WHY IT EXISTS. docx.js already knew how to translate a Word file without
 * rebuilding it: a .docx is a zip of XML, the visible words live in <w:t>
 * nodes, and swapping those nodes leaves every style, table, image and page
 * break untouched because none of them was ever read. A .pptx is the SAME
 * zip with the SAME idea under different tag names — <a:r>/<a:t>/<a:p> instead
 * of <w:r>/<w:t>/<w:p>. Writing that twice is how two copies of one rule start
 * answering differently without anyone noticing, so the rule lives here once
 * and both adapters name their tags.
 *
 * WHAT THE RULE IS, in full, because it is the hard part:
 *
 *   1. A sentence is rarely one node. Word and PowerPoint split a run wherever
 *      anything changes — a bold word, a language attribute, a spellcheck
 *      marker, a dirty flag left by an editor — so "translate each text node"
 *      ships fragments to the translator and gets nonsense back. Adjacent runs
 *      with IDENTICAL formatting are merged first; that heals the arbitrary
 *      splits and leaves only the real ones.
 *   2. A paragraph left with one lettered run translates in place — perfect.
 *   3. A paragraph left with several has genuine inline emphasis. There is no
 *      lossless answer: word order moves under translation, so emphasis cannot
 *      follow its word without re-aligning two languages. The whole paragraph
 *      is translated for context and placed in the longest run; the rest are
 *      emptied AND COUNTED, so the tool can say how many paragraphs lost inline
 *      emphasis instead of pretending nothing happened.
 *
 * Runs with no letters (numbering, "1. ", whitespace, symbols) are never sent
 * anywhere: they are not language.
 *
 * ⚠️ Deliberately string-level, not DOM. A DOMParser round-trip re-serialises
 * the whole part and quietly rewrites namespaces, attribute order and
 * self-closing tags. The applications tolerate that; other readers and diffing
 * tools do not, and the file stops being "the same document with new words".
 * The round-trip invariant the adapters assert — "markup outside the text node
 * is byte-identical" — is only true because nothing here parses.
 *
 * Exposed as window.DTOoxml.
 */
(function (root) {
    'use strict';
    if (root.DTOoxml) return;

    var HAS_LETTER = /\p{L}/u;

    /**
     * A namespace description. `preserve` is the only behavioural difference
     * between the two formats: WordprocessingML drops leading and trailing
     * spaces unless xml:space says otherwise, DrawingML keeps them always.
     */
    function ns(run, text, paraEnd, props, preserve) {
        return { run: run, text: text, paraEnd: paraEnd, props: props, preserve: !!preserve };
    }

    var W = ns('w:r', 'w:t', '</w:p>', 'w:rPr', true);   // Word
    var A = ns('a:r', 'a:t', '</a:p>', 'a:rPr', false);  // PowerPoint / DrawingML

    function esc(tag) { return tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

    /* `<w:r …>` must not match `<w:rPr …>`: the char after the tag name has to
       be whitespace or `>`. Same for the closing tag — `</w:r>` is not the
       prefix of `</w:rPr>` because `>` must follow immediately. That single
       detail is why a naive tag match silently eats run properties. */
    function runRe(n) {
        return new RegExp('<' + esc(n.run) + '(?:\\s[^>]*)?>[\\s\\S]*?<\\/' + esc(n.run) + '>', 'g');
    }
    function textRe(n) {
        return new RegExp('(<' + esc(n.text) + '(?:\\s[^>]*)?>)([\\s\\S]*?)(<\\/' + esc(n.text) + '>)');
    }
    /* Run properties come both paired (`<w:rPr>…</w:rPr>`) and self-closing
       with attributes (`<a:rPr lang="en-US" dirty="0"/>`), and PowerPoint
       writes the second form far more often than the first. A regex that only
       knows the paired form reads every PowerPoint run as "no formatting",
       merges runs that differ, and loses the distinction this whole file is
       built on. */
    function propsRe(n) {
        return new RegExp('<' + esc(n.props) + '(?:\\s[^>]*)?(?:\\/>|>[\\s\\S]*?<\\/' + esc(n.props) + '>)');
    }

    // ── XML text helpers ─────────────────────────────────────────────────

    function unescapeXml(s) {
        return String(s)
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
            .replace(/&#(\d+);/g, function (_, d) { return String.fromCodePoint(+d); })
            .replace(/&#x([0-9a-f]+);/gi, function (_, h) { return String.fromCodePoint(parseInt(h, 16)); })
            .replace(/&amp;/g, '&');   // last: an escaped &amp;lt; must stay &lt;
    }

    function escapeXml(s) {
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    /**
     * Keep the ORIGINAL opening tag; only upgrade it when the new text needs
     * `xml:space="preserve"` and it is missing.
     *
     * Recomputing the tag from the translated text looks equivalent and is not:
     * a run whose source had the attribute but whose translation happens not to
     * start with a space would silently lose it, and the rebuilt part then
     * differs from the original OUTSIDE the text nodes — which is precisely the
     * promise these adapters make.
     */
    function openTagFor(n, originalTag, text) {
        if (!n.preserve) return originalTag;
        if (!/^\s|\s$/.test(text)) return originalTag;
        if (/xml:space\s*=/.test(originalTag)) return originalTag;
        return originalTag.replace(new RegExp('^<' + esc(n.text)), '<' + n.text + ' xml:space="preserve"');
    }

    // ── parsing ──────────────────────────────────────────────────────────

    /**
     * Split a part into an ordered list of pieces so it can be rebuilt exactly.
     * Every piece is either raw XML (untouched) or a run whose text we own.
     */
    function splitRuns(xml, n) {
        n = n || W;
        var pieces = [];
        var re = runRe(n);
        var tRe = textRe(n);
        var pRe = propsRe(n);
        var last = 0, m;
        while ((m = re.exec(xml)) !== null) {
            if (m.index > last) pieces.push({ raw: xml.slice(last, m.index) });
            var run = m[0];
            var tm = run.match(tRe);
            if (tm) {
                pieces.push({
                    run: run,
                    pre: run.slice(0, tm.index),
                    post: run.slice(tm.index + tm[0].length),
                    openTag: tm[1],
                    text: unescapeXml(tm[2]),
                    rPr: (run.match(pRe) || [''])[0].replace(/\s+/g, ''),
                });
            } else {
                pieces.push({ raw: run });
            }
            last = m.index + m[0].length;
        }
        if (last < xml.length) pieces.push({ raw: xml.slice(last) });
        return pieces;
    }

    /** Paragraph boundaries, so emphasis handling stays inside one paragraph. */
    function paragraphOf(pieces, n) {
        var groups = [];
        var cur = [];
        pieces.forEach(function (p, i) {
            cur.push(i);
            if (p.raw && p.raw.indexOf(n.paraEnd) >= 0) { groups.push(cur); cur = []; }
        });
        if (cur.length) groups.push(cur);
        return groups;
    }

    /**
     * Build the translatable units for one part.
     * Returns { pieces, units } where each unit names the piece indices it owns.
     */
    function unitsForPart(xml, partName, n) {
        n = n || W;
        var pieces = splitRuns(xml, n);
        var units = [];

        paragraphOf(pieces, n).forEach(function (idxs) {
            var runIdx = idxs.filter(function (i) { return pieces[i].run !== undefined; });
            if (!runIdx.length) return;

            // 1) merge adjacent runs with identical formatting
            var merged = [];
            runIdx.forEach(function (i) {
                var prev = merged[merged.length - 1];
                var contiguous = prev && prev.idxs[prev.idxs.length - 1] === i - 1;
                if (prev && contiguous && prev.rPr === pieces[i].rPr) {
                    prev.idxs.push(i);
                    prev.text += pieces[i].text;
                } else {
                    merged.push({ idxs: [i], text: pieces[i].text, rPr: pieces[i].rPr });
                }
            });

            var lettered = merged.filter(function (g) { return HAS_LETTER.test(g.text); });
            if (!lettered.length) return;

            if (lettered.length === 1) {
                units.push({
                    part: partName, idxs: lettered[0].idxs,
                    text: lettered[0].text, flattened: false,
                });
                return;
            }

            // 3) genuine inline emphasis — translate the paragraph as a whole
            var whole = merged.map(function (g) { return g.text; }).join('');
            var longest = lettered.reduce(function (a, b) { return b.text.length > a.text.length ? b : a; });
            units.push({
                part: partName,
                idxs: longest.idxs,
                text: whole,
                flattened: true,
                blank: lettered.filter(function (g) { return g !== longest; })
                    .reduce(function (acc, g) { return acc.concat(g.idxs); }, []),
            });
        });

        return { pieces: pieces, units: units };
    }

    /** Write a translation into the pieces a unit owns. */
    function placeTranslation(pieces, unit, text) {
        var keep = unit.idxs[0];
        pieces[keep] = Object.assign({}, pieces[keep], { text: String(text) });
        // Every other piece it merged with is emptied, or the original text
        // would survive alongside the translation.
        unit.idxs.slice(1).forEach(function (i) {
            pieces[i] = Object.assign({}, pieces[i], { text: '' });
        });
        (unit.blank || []).forEach(function (i) {
            pieces[i] = Object.assign({}, pieces[i], { text: '' });
        });
    }

    /** Serialise pieces back to a part — raw markup byte for byte. */
    function buildPart(pieces, n) {
        n = n || W;
        var close = '</' + n.text + '>';
        return pieces.map(function (p) {
            if (p.raw !== undefined) return p.raw;
            return p.pre + openTagFor(n, p.openTag, p.text) + escapeXml(p.text) + close + p.post;
        }).join('');
    }

    root.DTOoxml = {
        W: W, A: A, ns: ns,
        splitRuns: splitRuns,
        unitsForPart: unitsForPart,
        placeTranslation: placeTranslation,
        buildPart: buildPart,
        escapeXml: escapeXml,
        unescapeXml: unescapeXml,
        openTagFor: openTagFor,
        HAS_LETTER: HAS_LETTER,
    };
})(typeof self !== 'undefined' ? self : this);
